import { forwardRef, useImperativeHandle, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Trans } from 'react-i18next';

import {
  CAPTCHA_ENABLED_ENVS,
  GOOGLE_PRIVACY_POLICY_URL,
  GOOGLE_TERMS_OF_SERVICE_URL,
  TEST_RECAPTCHA_TOKEN
} from 'constants/commons';

import styles from './ReCaptcha.module.scss';

interface ReCaptchaProps {
  onChange: (value: string | null) => Promise<void>;
  onError?: (error: Error | undefined) => void;
  onExpired?: () => void;
  onLoad?: () => void;
}

export interface ReCaptchaRef {
  execute: () => void;
  reset: () => void;
}

const env = import.meta.env.VITE_ENV;
const isCaptchaEnabled = CAPTCHA_ENABLED_ENVS.includes(env);

const ReCaptcha = forwardRef<ReCaptchaRef, ReCaptchaProps>(
  ({ onChange, onError, onExpired, onLoad }, ref) => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const key = import.meta.env.VITE_RECAPTCHA_KEY;

    useImperativeHandle(ref, () => ({
      execute: (): void => {
        if (!isCaptchaEnabled) {
          void onChange(TEST_RECAPTCHA_TOKEN);
          return;
        }
        recaptchaRef.current?.execute();
      },
      reset: (): void => {
        if (isCaptchaEnabled) {
          recaptchaRef.current?.reset();
        }
      }
    }));

    const handleExpired = (): void => {
      if (isCaptchaEnabled) {
        recaptchaRef.current?.reset();
      }
      onExpired?.();
    };

    if (!isCaptchaEnabled) {
      return null;
    }

    return (
      <div className={styles.container}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={key}
          size="invisible"
          onChange={onChange}
          onErrored={onError}
          onExpired={handleExpired}
          onLoad={onLoad}
          badge="bottomright"
          theme="light"
        />
        <p className={styles.notice}>
          <Trans
            i18nKey="auth.recaptcha.notice"
            components={{
              privacyLink: (
                <a
                  href={GOOGLE_PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.link}
                />
              ),
              termsLink: (
                <a
                  href={GOOGLE_TERMS_OF_SERVICE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.link}
                />
              )
            }}
          />
        </p>
      </div>
    );
  }
);

ReCaptcha.displayName = 'ReCaptcha';

export default ReCaptcha;
